"use client";

import { useEffect, useMemo, useState } from "react";
import cnBind from "classnames/bind";
import {
    CreditCard,
    Building2,
    Handshake,
    LayoutGrid,
    LogOut,
    Menu,
    MessageCircle,
    Store,
    User,
    X,
} from "lucide-react";
import { usePathname } from "next/navigation";

import { AdminNotificationsBell } from "@/components/AdminNotificationsBell/AdminNotificationsBell";
import { PageHeadSync } from "@/components/PageHeadSync/PageHeadSync";
import { CABINET_NAV, cabinetPageTitle, cabinetSectionFromPath } from "@/layouts/cabinetNav";
import { useAuth } from "@/shared/auth/AuthContext";
import { userHasPartnerRole } from "@/shared/auth/partnerAccess";
import { userHasStaffRole } from "@/shared/auth/staffAccess";
import type { PageLayoutProps } from "./types";
import type { MouseEvent as ReactMouseEvent } from "react";

import styles from "./CabinetLayout.module.scss";

const cx = cnBind.bind(styles);

const ICONS = {
    services: Store,
    publish: LayoutGrid,
    billing: CreditCard,
    company: Building2,
    profile: User,
    partner: Handshake,
} as const;

const MANAGER_URL = process.env.NEXT_PUBLIC_TEZAM_MANAGER_URL || "https://t.me/tezamoffical_bot";
const TAB_IDS = ["services", "billing", "company", "profile"] as const;

export function CabinetLayout({ children, title, description }: PageLayoutProps) {
    const { logout, user } = useAuth();
    const pathname = usePathname() ?? "/cabinet";
    const isCreate = pathname.startsWith("/cabinet/create");
    const section = cabinetSectionFromPath(pathname);
    const pageTitle = cabinetPageTitle(pathname);
    const [mobileOpen, setMobileOpen] = useState(false);
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://tezam.by";
    const canonicalUrl = new URL(pathname.split("?")[0] || "/cabinet", siteUrl).toString();
    const defaultOg = new URL("/favicon.svg", siteUrl).toString();
    const hasPartner = userHasPartnerRole(user);
    const displayName = user?.fullName?.trim() || user?.email?.split("@")[0] || "Клиент";

    const nav = useMemo(
        () => CABINET_NAV.filter((item) => !item.partnerOnly || hasPartner),
        [hasPartner],
    );
    const tabs = useMemo(() => nav.filter((item) => TAB_IDS.includes(item.id as (typeof TAB_IDS)[number])), [nav]);

    const closeMobile = () => setMobileOpen(false);

    /** Soft App Router nav inside cabinet often updates the URL but keeps the old page mounted. */
    const goCabinet = (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => {
        event.preventDefault();
        event.stopPropagation();
        const current = (pathname || "/cabinet").split("?")[0];
        const next = href.split("?")[0];
        if (current === next) {
            closeMobile();
            return;
        }
        // Full document navigation — do not soft-route via Next Link / App Router.
        window.location.assign(href);
    };

    useEffect(() => {
        setMobileOpen(false);
    }, [pathname]);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const prev = {
            htmlOverflow: html.style.overflow,
            htmlHeight: html.style.height,
            bodyOverflow: body.style.overflow,
            bodyHeight: body.style.height,
        };
        html.style.overflow = "hidden";
        html.style.height = "100%";
        body.style.overflow = "hidden";
        body.style.height = "100%";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            html.style.height = prev.htmlHeight;
            body.style.overflow = prev.bodyOverflow;
            body.style.height = prev.bodyHeight;
        };
    }, []);

    const navLinks = (
        <>
            {nav.map((item) => {
                const Icon = ICONS[item.id] ?? Store;
                const active = isCreate ? item.id === "services" : section === item.id;
                return (
                    <a
                        key={item.id}
                        href={item.href}
                        className={cx("navItem", active && "navItemOn")}
                        onClick={(e) => goCabinet(e, item.href)}
                    >
                        <Icon size={18} strokeWidth={2} aria-hidden />
                        <span className={cx("navText")}>
                            <span className={cx("navLabel")}>{item.label}</span>
                            <span className={cx("navHint")}>{item.hint}</span>
                        </span>
                    </a>
                );
            })}
        </>
    );

    return (
        <>
            <PageHeadSync
                title={title || `${pageTitle} | Tezam`}
                description={description || "Кабинет клиента Tezam: сервисы, счета, реквизиты и профиль."}
                canonicalUrl={canonicalUrl}
                ogImage={process.env.NEXT_PUBLIC_OG_IMAGE_URL ?? defaultOg}
                robots="noindex,nofollow,noarchive,nosnippet"
            />
            <div className={cx("shell", isCreate && "shellCreate", !isCreate && "hasTabbar")}>
                {mobileOpen ? (
                    <button type="button" className={cx("scrim")} aria-label="Закрыть меню" onClick={closeMobile} />
                ) : null}

                <aside className={cx("sidebar", mobileOpen && "sidebarOpen")} aria-label="Разделы кабинета">
                    <div className={cx("sidebarHead")}>
                        <a href="/" className={cx("brand")} aria-label="TEZAM — на главную" onClick={closeMobile}>
                            <img src="/images/tezam-logo.png" alt="TEZAM" />
                        </a>
                        <button type="button" className={cx("sidebarClose")} onClick={closeMobile} aria-label="Закрыть меню">
                            <X size={20} />
                        </button>
                    </div>

                    <p className={cx("sidebarKicker")}>Кабинет</p>
                    <nav className={cx("nav")}>{navLinks}</nav>

                    <a
                        className={cx("navItem", "navSupport")}
                        href={MANAGER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <MessageCircle size={18} strokeWidth={2} aria-hidden />
                        <span className={cx("navText")}>
                            <span className={cx("navLabel")}>Поддержка</span>
                            <span className={cx("navHint")}>Менеджер-бот</span>
                        </span>
                    </a>

                    <div className={cx("sidebarFoot")}>
                        <button type="button" className={cx("logout")} onClick={() => void logout()}>
                            <LogOut size={16} aria-hidden />
                            Выйти
                        </button>
                        <p className={cx("copy")}>© Tezam</p>
                    </div>
                </aside>

                <div className={cx("stage")}>
                    <header className={cx("topbar")}>
                        <button
                            type="button"
                            className={cx("menuBtn")}
                            onClick={() => setMobileOpen(true)}
                            aria-label="Открыть меню"
                        >
                            <Menu size={20} />
                        </button>
                        <div className={cx("topbarTitle")}>
                            <span>{pageTitle}</span>
                            {displayName ? <span className={cx("topbarUser")}> · {displayName}</span> : null}
                        </div>
                        <div className={cx("topbarActions")}>
                            {user && !userHasStaffRole(user) ? (
                                <AdminNotificationsBell variant="toolbar" inboxHref="/cabinet/notifications" />
                            ) : null}
                        </div>
                    </header>
                    <main className={cx("body", isCreate && "bodyCreate")}>{children}</main>
                    {isCreate ? null : (
                        <nav className={cx("tabbar")} aria-label="Разделы кабинета">
                            {tabs.map((item) => {
                                const Icon = ICONS[item.id] ?? Store;
                                const active = section === item.id;
                                return (
                                    <a
                                        key={item.id}
                                        href={item.href}
                                        className={cx("tab", active && "tabOn")}
                                        aria-current={active ? "page" : undefined}
                                        onClick={(e) => goCabinet(e, item.href)}
                                    >
                                        <Icon size={20} strokeWidth={active ? 2.4 : 2} aria-hidden />
                                        <span>{item.tabLabel}</span>
                                    </a>
                                );
                            })}
                            <button
                                type="button"
                                className={cx("tab", (mobileOpen || section === "partner") && "tabOn")}
                                onClick={() => setMobileOpen(true)}
                                aria-label="Ещё"
                            >
                                <Menu size={20} strokeWidth={2} aria-hidden />
                                <span>Ещё</span>
                            </button>
                        </nav>
                    )}
                </div>
            </div>
        </>
    );
}
